import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton, Badge } from '@bloomcode/ui';
import { RefreshCw, FileText, TrendingUp, AlertCircle, Calendar } from '@bloomcode/icons';
import { useCookieSession } from '@bloomcode/auth';
import { useInvoiceSummary } from '@/hooks';
import type { PaymentStatus } from '@bloomcode/graphql';

function StatCard({
  title,
  value,
  icon: Icon,
  loading,
  description,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  loading: boolean;
  description?: string;
}) {
  return (
    <Card variant="glass" hover="lift">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <p className="text-2xl font-bold font-[Manrope]">{value}</p>
        )}
        {description && !loading && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

function statusBadgeVariant(
  status: PaymentStatus
): 'default' | 'success' | 'destructive' | 'warning' {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'OVERDUE':
      return 'destructive';
    case 'OPEN':
      return 'warning';
    default:
      return 'default';
  }
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(
    amount / 100
  );
}

export default function DashboardPage() {
  const { session } = useCookieSession();
  const navigate = useNavigate();
  const {
    totalOutstanding,
    paidThisMonth,
    overdueCount,
    nextDue,
    recentInvoices,
    loading,
    refetch,
  } = useInvoiceSummary();

  const firstName = (session?.name || session?.email || 'there').split(' ')[0].split('@')[0];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const currency = recentInvoices[0]?.currency || 'INR';

  return (
    <>
      <Helmet>
        <title>Dashboard | Bloomcode Ledger Portal</title>
        <meta
          name="description"
          content="View your account summary, outstanding invoices, and recent activity."
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="mb-8 flex items-start justify-between">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
          ) : (
            <div>
              <h1 className="text-3xl font-bold font-[Manrope]">
                {getGreeting()}, {firstName}!
              </h1>
              <p className="mt-1 text-muted-foreground">Here's your account summary.</p>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Outstanding"
            value={loading ? '—' : formatCurrency(totalOutstanding, currency)}
            icon={FileText}
            loading={loading}
            description="Across all open invoices"
          />
          <StatCard
            title="Paid This Month"
            value={loading ? '—' : formatCurrency(paidThisMonth, currency)}
            icon={TrendingUp}
            loading={loading}
          />
          <StatCard
            title="Overdue Invoices"
            value={loading ? '—' : String(overdueCount)}
            icon={AlertCircle}
            loading={loading}
            description={overdueCount > 0 ? 'Requires attention' : 'All clear'}
          />
          <StatCard
            title="Next Payment Due"
            value={
              loading
                ? '—'
                : nextDue?.due_date
                  ? new Date(nextDue.due_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                    })
                  : 'None'
            }
            icon={Calendar}
            loading={loading}
            description={nextDue ? formatCurrency(nextDue.amount_due, nextDue.currency) : undefined}
          />
        </div>

        {/* Recent Invoices */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Invoices</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : recentInvoices.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No invoices found.</p>
            ) : (
              <div className="divide-y">
                {recentInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between py-3 cursor-pointer hover:bg-muted/50 px-2 rounded-md transition-colors"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium">{invoice.invoice_number}</p>
                      <p className="text-xs text-muted-foreground">
                        {invoice.created_at
                          ? new Date(invoice.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </span>
                      <Badge variant={statusBadgeVariant(invoice.status as PaymentStatus)}>
                        {invoice.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
